import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { amlApi } from '../api/aml.api.js';

/**
 * Recording a suspicion from the screen where it was formed.
 *
 * An officer reviewing a payment has every piece of evidence in front of them
 * — the screening result, what this customer has done with this beneficiary,
 * what they said they would do at onboarding — and until now had nowhere to
 * put a conclusion except refusing the payment. Refusing is a different
 * decision, it tells compliance nothing, and it is visible to the customer.
 *
 * This is not visible to the customer, and that is the point. Tipping off is
 * prohibited, so raising a suspicion does not touch the payment, its status,
 * or anything the customer can see. The payment is then approved or refused on
 * its own merits, separately.
 */

const SEVERITIES = [
  { value: 'LOW', label: 'Low — worth recording' },
  { value: 'MEDIUM', label: 'Medium — should be looked at' },
  { value: 'HIGH', label: 'High — look at this soon' },
  { value: 'CRITICAL', label: 'Critical — look at this now' },
] as const;

/** A financial intelligence unit cannot act on "looks odd". */
const MIN_NARRATIVE = 20;

export function RaiseSuspicionPanel({
  customerId,
  withdrawalId,
}: {
  customerId: string;
  withdrawalId: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState<string>('MEDIUM');
  const [narrative, setNarrative] = useState('');
  const [raised, setRaised] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const raise = useMutation({
    mutationFn: () =>
      amlApi.raiseSuspicion({
        customerId,
        withdrawalId,
        severity,
        narrative: narrative.trim(),
      }),
    onSuccess: (result) => {
      setOpen(false);
      setNarrative('');
      setRaised(result.alertId);
      setError(null);
    },
    onError: (err: unknown) => {
      setOpen(false);
      setError(err instanceof Error ? err.message : 'The suspicion could not be recorded.');
    },
  });

  return (
    <Stack spacing={1}>
      {raised !== null && (
        <Alert severity="success">
          Recorded and sent to the compliance queue. The customer is not told, and this payment is
          unchanged — approve or refuse it on its own merits.
        </Alert>
      )}
      {error !== null && <Alert severity="error">{error}</Alert>}
      <Button
        size="small"
        color="warning"
        variant="outlined"
        sx={{ alignSelf: 'flex-start' }}
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        Raise a suspicion
      </Button>

      <Dialog
        open={open}
        onClose={() => {
          if (!raise.isPending) setOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Raise a suspicion</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              This goes to the compliance queue, where it can be escalated to a SAR. It does not
              change this payment and the customer is not told — telling them would be tipping
              off.
            </Typography>
            <TextField
              select
              label="How urgent"
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
            >
              {SEVERITIES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="What made you suspicious"
              value={narrative}
              onChange={(e) => setNarrative(e.target.value)}
              multiline
              minRows={4}
              required
              inputProps={{ maxLength: 4096 }}
              helperText={
                narrative.trim().length < MIN_NARRATIVE
                  ? `What you write here is what a SAR would eventually rest on. ${
                      MIN_NARRATIVE - narrative.trim().length
                    } more characters.`
                  : 'Recorded with your name and the payment you were looking at.'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={raise.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={raise.isPending || narrative.trim().length < MIN_NARRATIVE}
            onClick={() => raise.mutate()}
          >
            Record it
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
